import TreeMapIcon from 'assets/icons/mapping-icons/TreeMapIcon';
import AddedByReviewedByCard from 'components/BioRegion/AddedByReviewedByCard';
import { CommentsCard, ReviewCommentsCard } from 'components/BioRegion/CommentsCard';
import Gallery from 'components/Gallery';
import LocationMapCard from 'components/Map/MapAndLocationName';
import MultilingualText from 'components/Multilingual/MultilingualText';
import ContextHelp from 'components/common/ContextHelp';
import { UserContext } from 'contexts/user/UserContextProvider';
import IntlMessages from 'helpers/IntlMessages';
import { setTabFromLocation } from 'helpers/utils-typescript';
import SpecimenBannerCard from 'pages/Bioregion/Specimen/SpecimenBannerCard';
import SpecimenOverviewTabDesktop from 'pages/Bioregion/Specimen/SpecimenOverviewTabDesktop';
import { MapMarkerNumber } from 'pages/Commons/Quest/RouteMap';
import { getOsmTagIconAndName, OsmTagType } from 'pages/Mapping/SelectPoi';
import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Card, Row, Tab, Tabs } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { Link, RouteProps, useHistory, useLocation, useParams } from 'react-router-dom';
import { Speciman } from 'services/graphqltypes';

type SpecimenDetailsDesktopLayoutProps = {
  specimanBySlug: Speciman;
  hasEditPermission: boolean;
};

interface SpecimenRouteParams {
  commonsSlug?: string;
}

/** This component renders the page with all details about a Specimen. It contains both desktop and mobile layout: The mobile layout is still used for specimens under review. */
const SpecimenDetailsDesktopLayout = ({
  specimanBySlug,
  hasEditPermission }: SpecimenDetailsDesktopLayoutProps) => {
  const { commonsSlug } = useParams<SpecimenRouteParams>();
  const { hash, state } = useLocation<{ from: RouteProps['location'], breadcrumbsTranslationId: string }>();
  const history = useHistory();
  const intl = useIntl();
  const { isAuthenticated, currentUser } = useContext(UserContext);

  const { goBackLink, breadcrumbsTranslationId } = useMemo(() => {
    // If we have a previous location, use it
    if (state?.from) {
      return {
        goBackLink: state.from,
        breadcrumbsTranslationId: state.breadcrumbsTranslationId || 'general.pois'
      };
    }
    
    // Otherwise, determine based on commonsSlug
    return {
      goBackLink: commonsSlug ? `/commons/${commonsSlug}#specimens` : '/map',
      breadcrumbsTranslationId: commonsSlug ? 'general.pois' : 'specimen.map'
    };
  }, [commonsSlug, state]);
  /* */
  /* Tabs */
  /* */
  const tabs = useMemo(() => { return ['overview', 'discussion', 'notes', 'gallery',]; }, []);
  const [activeTab, setActiveTab] = useState(tabs[0]);
  useEffect(() => {
    const hashForTab = tabs.map((tab: string) => `#${tab}`).includes(hash) ? hash : tabs[0]; // redirect to overview tab if hash is not valid
    if (hashForTab) { setTabFromLocation(hashForTab, tabs, setActiveTab); }
  }, [hash, tabs]);
  const handleTabChange = useCallback((tabName: string) => { history.push(`#${tabName}`); }, [history]);

  /* */
  /* Render */
  /* */
  return (
    <Fragment>
      {/* Breadcrumb Desktop layout */}
      <nav className='d-none d-md-block mb-0 mt-md-n3 mt-xl-0'>
        <ol className="breadcrumb px-0 pt-0">
          <li className="breadcrumb-item">
            <Link to={goBackLink}>
              <IntlMessages id={breadcrumbsTranslationId} /> { /* pass a translationId in router state to change the text in the breadcrumb */}
            </Link>
          </li>
          <li className="breadcrumb-item active">
            <MultilingualText value={specimanBySlug.name} />
          </li>
        </ol>
      </nav>

      <Row className='ml-xl-3'>
        <div className="col-md-8 col-12 order-2 order-md-1">
          <Tabs
            id="specimenPublicPageTabs"
            activeKey={activeTab}
            onSelect={handleTabChange}
            className="separator-tabs mb-3 tabs scrollable-tabs"
            mountOnEnter={true}
          >

            {/* overview tab */}
            <Tab eventKey={tabs[0]} title={intl.formatMessage({ id: 'bioregion.overview' })} tabClassName="text-normal">
              <SpecimenOverviewTabDesktop
                specimen={specimanBySlug as Speciman}
              />
            </Tab>

            {/* comments tab */}
            <Tab eventKey={tabs[1]} title={intl.formatMessage({ id: 'general.discussion' })} tabClassName="text-normal">

              {/* review comments */}
              {!['DRAFT', 'APPROVED_PUBLISHED', 'MAPPING_DRAFT'].includes(specimanBySlug.status) &&
                <ReviewCommentsCard speciman={specimanBySlug} />
              }
              { /* everybody's comments, after specimen has been published*/
                /* show to logged in users, and to non-logged in, if there are > 0 comments already */
                specimanBySlug.status === 'APPROVED_PUBLISHED' &&
                (isAuthenticated ? specimanBySlug.speciesSpecimenCommentsBySpecimenId?.nodes.length > 0 &&
                  <CommentsCard speciman={specimanBySlug} /> : 'Sign in to see more')}
            </Tab>

            {/* only show notes tab for drafts */}
            {['DRAFT', 'MAPPING_DRAFT'].includes(specimanBySlug.status) &&
              <Tab eventKey={tabs[2]} title={intl.formatMessage({ id: 'specimen.notes' })} tabClassName="text-normal">
                {/* only show notes card if there is any */}
                {specimanBySlug.notes ?
                  <Card className="mb-3 w-100">
                    <Card.Body className='px-3 py-2'>
                      <div className='row no-gutters'>
                        <div className='h4 my-2 mx-2 color-theme-1'>
                          <IntlMessages id='specimen.notes' />
                        </div>
                        <div className='ml-auto'>
                          <ContextHelp contextHelpId='5155d253-47d9-4696-92d3-38193736ca07' />
                        </div>
                      </div>
                    </Card.Body>
                    <Card.Body className='pt-0 mx-2 px-3'>
                      {specimanBySlug.notes}
                    </Card.Body>
                  </Card> :
                  <h2>
                    <IntlMessages id='general.loading-no-data' />
                  </h2>
                }
              </Tab>
            }

            <Tab eventKey={tabs[3]} title={intl.formatMessage({ id: 'general.gallery' })} tabClassName="text-normal">
              <Gallery images={(specimanBySlug.specimenAttachmentsBySpecimenId as any).nodes} />
            </Tab>
          </Tabs>
        </div>

        <div className="col-md-4 col-12 order-1 order-md-2 px-xxl-5">
          <SpecimenBannerCard
            speciman={specimanBySlug}
            showEditDashboardButton={hasEditPermission}
            // current user can review the speciman if he is territory mentor and not the author of a specimen that is not draft or published
            showReviewerButtons={!['DRAFT', 'APPROVED_PUBLISHED', 'MAPPING_DRAFT'].includes(specimanBySlug.status) && (currentUser.isTerritoryMentor) && (currentUser?.id !== specimanBySlug.createdByUserId)}
          />

          {/* desktop view, mobile view is under overview tab*/}
          <div className='d-none d-md-block'>
            <LocationMapCard
              mapHeight='300px'
              district={specimanBySlug.district as JSON}
              coordinates={[specimanBySlug.coordinates?.x, specimanBySlug.coordinates?.y]}
              markerIcon={
                specimanBySlug.type !== 'POI'
                  ? <TreeMapIcon size={40} />
                  : <MapMarkerNumber // this is not really working out with the offset here; the offset should be added as LocationMapCard component
                    number={getOsmTagIconAndName(specimanBySlug.osmTags as OsmTagType, '30px').icon}
                    color='white'
                    borderColor='#60b6aa' // ARK green
                    width='40px'
                    height='40px'
                  />
              }
            />
            <AddedByReviewedByCard specimanBySlug={specimanBySlug} />
          </div>
        </div>

      </Row>
    </Fragment>

  );
};

export default SpecimenDetailsDesktopLayout;
